import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAppStore } from './appStore';

interface User {
    id: string;
    email: string;
    name: string;
    picture: string;
}

interface GoogleAuthState {
    user: User | null;
    isSignedIn: boolean;
    isLoading: boolean;

    signInWithCredential: (credential: string) => Promise<boolean>;
    signOut: () => Promise<void>;
    checkAuth: () => Promise<void>;
}

export const useGoogleAuth = create<GoogleAuthState>()(
    persist(
        (set) => ({
            user: null,
            isSignedIn: false,
            isLoading: true,

            signInWithCredential: async (credential: string) => {
                try {
                    const response = await fetch('/api/auth/google', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ credential })
                    });
                    if (!response.ok) return false;
                    const data = await response.json();
                    set({ user: data.user, isSignedIn: true, isLoading: false });
                    return true;
                } catch {
                    return false;
                }
            },

            signOut: async () => {
                try {
                    await fetch('/api/auth/logout', {
                        method: 'POST',
                        credentials: 'include'
                    });
                } catch (e) {
                    console.error('Logout failed:', e);
                }
                // Cloud is authoritative. Do not leave authenticated family data
                // visible in this browser after an explicit sign-out.
                useAppStore.getState().clearAccountData();
                set({ user: null, isSignedIn: false });
            },

            // Check auth status on app load
            checkAuth: async () => {
                set({ isLoading: true });
                try {
                    const response = await fetch('/api/auth/me', {
                        credentials: 'include'
                    });
                    const data = await response.json();

                    if (data.authenticated && data.user) {
                        set({
                            user: data.user,
                            isSignedIn: true,
                            isLoading: false
                        });
                    } else {
                        set({
                            user: null,
                            isSignedIn: false,
                            isLoading: false
                        });
                    }
                } catch (e) {
                    console.error('Auth check failed:', e);
                    set({
                        user: null,
                        isSignedIn: false,
                        isLoading: false
                    });
                }
            }
        }),
        {
            name: 'kidstube-google-auth',
            partialize: (state) => ({
                // Only persist user info, not auth state (that's server-side now)
                user: state.user
            }),
            onRehydrateStorage: () => (state) => {
                // After rehydration, check with server if actually authenticated
                if (state) {
                    state.checkAuth();
                }
            }
        }
    )
);

// Export for backward compatibility
export { useGoogleAuth as useGoogleAuthStore };
